import { useEffect, useRef, useState } from "react";
import { loadQueue, saveQueue, type QueuedMessage } from "./QueueStack";

export function useChatQueue(
  chatId: string | null,
  active: boolean,
  editing: boolean,
  onRelease: (message: QueuedMessage, markFailed: () => void) => void
) {
  const [queue, setQueue] = useState<QueuedMessage[]>(() =>
    chatId ? loadQueue(chatId) : []
  );
  const queueChatIdRef = useRef<string | null>(chatId);

  useEffect(() => {
    if (!chatId) return;
    const previousChatId = queueChatIdRef.current;
    if (previousChatId === chatId) {
      saveQueue(chatId, queue);
      return;
    }
    if (previousChatId === null) {
      queueChatIdRef.current = chatId;
      setQueue(loadQueue(chatId));
      return;
    }
    queueChatIdRef.current = chatId;
    saveQueue(previousChatId, []);
    saveQueue(chatId, queue);
  }, [chatId, queue]);

  useEffect(() => {
    if (active) {
      if (queue.some((m) => m.state === "sent"))
        setQueue((q) => q.filter((m) => m.state !== "sent"));
      return;
    }
    if (editing) return;
    const head = queue[0];
    if (!head || head.state) return;
    setQueue((q) =>
      q.map((m) => (m.id === head.id ? { ...m, state: "sent" } : m))
    );
    onRelease(head, () =>
      setQueue((q) => [
        { ...head, state: "failed" },
        ...q.filter((m) => m.id !== head.id),
      ])
    );
  }, [active, editing, queue, onRelease]);

  return [queue, setQueue] as const;
}
